/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import axios from 'axios';
import { feathers } from '@/feathers-client';
import { HIDE_ERROR_AFTER_MS } from '@/Core/utilities/index';
import { COMMUNICATIONS_MS_SEND_OPTIONS } from '../config/index';
import { soleProprietor, lowStandard, standard } from '../config/consoleSteps';
import {
  REGISTRATION_STATES,
  REGISTRATION_STATUSES_TEMPLATES,
} from '../config/registrationStatus';

const crmAPIPhishingError = 'Sorry, text message(s) could not be sent';
const commsAPIPhishingError = 'user flagged as phishing';
const regStatusCompleted = 'campaign_completed';

/**
 * Shapes the video variables into an object with shape { id, title }
 *
 * @param {String} videoVariable '{{mediavideo_<video_id>_<video_title>}}'
 * @returns {Object} { id: video_id, title: video_title }
 */
function shapeVideoVariables(videoVariable) {
  const videoIdIndex = 1;
  const videoTitleIndex = 2;
  const trailingBracketsRegExp = new RegExp('}} *$', 'g');

  const splitVideoVariable = videoVariable.split('_');
  const id = parseInt(splitVideoVariable[videoIdIndex], 10);
  const title = splitVideoVariable[videoTitleIndex].replace(trailingBracketsRegExp, '');
  return {
    id,
    title,
  };
}

/**
 * Appends the video information from Media table to video variable if exists
 *
 * @param {Object} message response row object from '/texts' service
 * @param {Array} formattedVideoVariables Array of objects: { id: video_id, title: video_title }
 * @returns {Array} Array of Objects, with additional fields thumbnail_url and image_url if exists
 */
function addFoundVideosInfo(message, formattedVideoVariables) {
  const foundVideoIds = message.videos.map(video => video.id);
  return formattedVideoVariables.map(formattedVideoVariable => {
    const matchedVideoIndex = foundVideoIds.indexOf(formattedVideoVariable.id);
    if (matchedVideoIndex !== -1) {
      return message.videos.find(video => video.id === foundVideoIds[matchedVideoIndex]);
    }
    return formattedVideoVariable;
  });
}

/**
 * Formats the videoVariable strings into objects and appends the video url information if available
 *
 * @param {Array} videoVariables array of strings: {{mediavideo_<video_id>_<video_title>}}
 * @param {Object} message response row object from '/texts' service
 * @returns {Array} Array fo objects { id, title, [thumbail_url], [image_url] }
 */
function formatVideos(videoVariables, message) {
  if (videoVariables) {
    const formattedVideoVariables = videoVariables.map(videoVariable => {
      return shapeVideoVariables(videoVariable);
    });
    if (message.videos.length) {
      return addFoundVideosInfo(message, formattedVideoVariables);
    }
    return formattedVideoVariables;
  }
  return [];
}

/**
 * Check if at least one number in a list of contacts belongs to a different country
 *
 * @param {Array} contacts list of contacts
 * @param {String} userTimezone logged user's time zone
 * @returns {Boolean} true if any of the contacts is in a different country
 */
function isAnyContactInAnotherCountry(contacts, userTimezone) {
  const isUserInAmerica = userTimezone.toLowerCase().includes('america');
  const AU_CODE = '+61';
  const US_CODE = '+1';
  const isDifferentCountry = contact => {
    return (
      (contact.mobile_phone.includes(AU_CODE) && isUserInAmerica) ||
      (contact.mobile_phone.includes(US_CODE) && !isUserInAmerica)
    );
  };
  return contacts.some(isDifferentCountry);
}

function validateContactAnotherCountry(commit, contacts, userTimezone) {
  if (userTimezone && isAnyContactInAnotherCountry(contacts, userTimezone)) {
    commit('setConversationsError', {
      error: true,
      message: 'Sorry, you can only text phone numbers within your country',
    });
    setTimeout(() => {
      commit('setConversationsError', { error: false, message: '' });
    }, HIDE_ERROR_AFTER_MS);
    return false;
  }
  return true;
}

function setConversationError(commit, errorDetail = '') {
  let errorMessage = 'Sorry, text message(s) could not be sent'
  if (errorDetail) {
    errorMessage = errorMessage.concat('. Error: ', errorDetail);
  }
  commit('setConversationsError', {
    error: true,
    message: errorMessage,
  });

  setTimeout(() => {
    commit('setConversationsError', { error: false, message: '' });
  }, HIDE_ERROR_AFTER_MS);
}

function setConversationSuccess(commit, msg) {
  commit('setConversationsSuccess', {
    success: true,
    message: msg,
  });
  commit('setSendingText', false);
  setTimeout(() => {
    commit('setConversationsSuccess', '');
  }, HIDE_ERROR_AFTER_MS);
}

function sendViaFeather(commit, state, contacts, payload) {
  return contacts.map(async contact => {
    await feathers
      .service('texts')
      .create({
        contact_id: contact.id,
        mobile_phone: contact.mobile_phone,
        body: payload.body,
        imagesURLs: payload.imagesURLs,
        skip_10dlc_validation: payload.skip_10dlc_validation,
        should_send_opt_in: contact.should_send_opt_in,
        should_send_original_sms: contact.should_send_original_sms,
      })
      .then(res => {
        commit('successfulText');
        const msg = `Successfully sent ${contacts.length - state.errorContacts.length
          } text message(s)`;
        if (!state.smsLimitModalVisible) {
          setConversationSuccess(commit, msg);
        }
      })
      .catch(error => {
        if (error.code === 403 && error.message === crmAPIPhishingError) {
          commit('setPhishingError', true);
        } else {
          setConversationError(commit, error.message || '');
        }
        commit('errorText', contact.id);
        commit('setSendingText', false);
        return error;
      });
  });
}

function hasAccessToFeature(state, feature) {
  if (!state.user) return false;
  const { features } = state.user;
  return features[feature] && features[feature].has_access;
}

export default {
  namespaced: true,
  state: {
    hasMoreChatTextsToLoad: false,
    hasMoreConversationsToLoad: true,
    conversationsInProgress: false,
    sendingTextInProgress: false, // Controls the loading animation if a text is still sending
    conversationsError: false,
    conversationsErrorMessage: '',
    conversationsSuccess: false,
    conversationsSuccessMessage: '',
    searchString: '',
    conversations: null,
    chatHistory: null,
    textsSent: 0,
    errorContacts: [],
    contactConversation: null,
    textMessageElements: {
      body: '',
      contacts: [],
      images: [],
    },
    smsLimitModalVisible: false,
    previousRequestPayload: {},
    has10dlcRegister: false,
    hasPhishingError: false,
    contactsOptInStatuses: [],
    contactOptInStatus: '',
    contactsOptInError: '',
    hasNonApproved: false,
    registrationType10dlc: '',
    registrationData10dlc: null,
    isRegistration10dlcIncomplete: false,
    registrationStatus10dlc: '',
    registrationStatusData10dlc: null,
    registrationDate10dlc: '',
    resendOTPisError: false,
    resendOTPError: '',
    resendOTPisSuccess: false,
    isLeadsPlusUser: false,
    isLeadsPlusContact: false,
    registrationBrandRetries: 0,
  },
  getters: {
    chatHistoryLimit(state, getters, rootState) {
      return rootState.preferences.conversationsLimit;
    },
    isSendButtonDisabled(state) {
      return (
        state.sendingTextInProgress ||
        !state.textMessageElements.body ||
        state.textMessageElements.body.length === 0
      );
    },
    statusData(state, getters, rootState) {
      if (!state.registrationStatus10dlc) return;

      const status = state.registrationStatus10dlc;

      if (
        state.registrationStatusData10dlc &&
        state.registrationStatusData10dlc.length &&
        state.registrationType10dlc === soleProprietor &&
        state.registrationStatusData10dlc.some(error => error.code && error.code === REGISTRATION_STATES.ERROR_CASE_30903)
      ) {
        return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.ERROR_CASE_30903];
      }

      if (!REGISTRATION_STATUSES_TEMPLATES[status]) {
        return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.DEFAULT_ERROR];
      }

      if (status == REGISTRATION_STATES.BRAND_TCR_FAILED && state.registrationType10dlc !== standard) {
        switch (state.registrationBrandRetries) {
          case 0:
            return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.BRAND_TCR_FAILED_RETRY_1];
          case 1:
            return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.BRAND_TCR_FAILED_RETRY_2];
          case 2:
            return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.BRAND_TCR_FAILED_RETRY_3];
          case 3:
            return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.BRAND_TCR_FAILED_RESTART];
        }
      }

      if (status == REGISTRATION_STATES.BRAND_TCR_FAILED) {
        switch (state.registrationBrandRetries) {
          case 0:
            return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.BRAND_TCR_FAILED_STANDARD_RETRY_1];
          case 1:
            return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.BRAND_TCR_FAILED_STANDARD_RETRY_2];
          case 2:
            return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.BRAND_TCR_FAILED_RESTART];
        }
      }

      if ((status == REGISTRATION_STATES.CAMPAIGN_TCR_FAILED || status == REGISTRATION_STATES.CAMPAIGN_TWILIO_FAILED) && state.registrationCampaignRetries >= 3) {
        return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.CAMPAIGN_FAILED_3_TIMES]
      }

      hasBulkTexts = hasAccessToFeature(rootState.userInformation, 'bulk-texts');
      if (hasBulkTexts && status == REGISTRATION_STATES.CAMPAIGN_COMPLETED) {
        return REGISTRATION_STATUSES_TEMPLATES[REGISTRATION_STATES.CAMPAIGN_COMPLETED_BULK_TEXTS];
      }

      return REGISTRATION_STATUSES_TEMPLATES[state.registrationStatus10dlc];
    },
  },
  mutations: {
    resetPreviousRequestPayload(state) {
      state.previousRequestPayload = {};
    },
    setSkipValidation(state) {
      state.previousRequestPayload.skip_10dlc_validation = true;
    },
    setRequestPayload(state, payload) {
      state.previousRequestPayload = payload;
    },
    setSmsLimitModalVisibility(state, payload) {
      state.smsLimitModalVisible = payload.isVisible;
    },
    clearErrors(state) {
      state.conversationsError = false;
      state.conversationsErrorMessage = '';
    },
    clearSuccess(state) {
      state.conversationsSuccess = false;
      state.conversationsSuccessMessage = '';
    },
    setConversations(state, conversationsList) {
      state.conversations = conversationsList;
      state.conversationsInProgress = false;
    },
    appendConversations(state, conversationsList) {
      state.conversations = [...state.conversations, ...conversationsList];
      state.conversationsInProgress = false;
    },
    setConversationsInProgress(state) {
      state.conversationsInProgress = true;
    },
    setSendingText(state, isSending) {
      state.sendingTextInProgress = isSending;
      if (isSending === false) {
        state.textsSent = 0;
        state.errorContacts = [];
      }
    },
    successfulText(state) {
      state.textsSent++;
    },
    errorText(state, contactId) {
      state.textsSent++;
      state.errorContacts = [...state.errorContacts, contactId];
    },
    setConversationsError(state, payload) {
      state.conversationsSuccess = false;
      state.conversationsError = payload.error;
      state.conversationsErrorMessage = payload.message;
      state.conversationsInProgress = false;
    },
    setConversationsSuccess(state, payload) {
      state.conversationsError = false;
      state.conversationsSuccess = payload.success;
      state.conversationsSuccessMessage = payload.message;
      state.conversationsInProgress = false;
    },
    setSearchString(state, payload) {
      state.searchString = payload;
    },
    setChatHistory(state, chatHistory) {
      state.chatHistory = chatHistory;
    },
    appendChatHistory(state, chatHistory) {
      state.chatHistory = [...state.chatHistory, ...chatHistory];
    },
    haveNotLoadedAllConversations(state) {
      state.hasMoreConversationsToLoad = true;
    },
    loadedAllConversations(state) {
      state.hasMoreConversationsToLoad = false;
    },
    haveNotLoadedAllChatTexts(state) {
      state.hasMoreChatTextsToLoad = true;
      state.conversationsInProgress = false;
    },
    loadedAllChatTexts(state) {
      state.hasMoreChatTextsToLoad = false;
      state.conversationsInProgress = false;
    },
    setContactConversation(state, conversation) {
      state.contactConversation = conversation;
    },
    clearChatHistory(state) {
      state.chatHistory = null;
    },
    setNewTextMessage(state, newTextMessage) {
      state.textMessageElements.body = newTextMessage;
    },
    setNewContactMessage(state, newContactMessage) {
      state.textMessageElements.contacts = [newContactMessage];
    },
    resetMessage(state) {
      state.textMessageElements.body = '';
    },
    setTextImages(state, textImages) {
      state.textMessageElements.images.push(textImages);
    },
    clearTextImages(state) {
      state.textMessageElements.images = [];
    },
    set10dlcRegister(state, value) {
      state.has10dlcRegister = value;
    },
    setPhishingError(state, value) {
      state.hasPhishingError = value;
    },
    setContactsOptInStatuses(state, value) {
      state.contactsOptInStatuses = value;
    },
    setContactOptInStatus(state, value) {
      state.contactOptInStatus = value;
    },
    setHasNonApproved(state, value) {
      state.hasNonApproved = value;
    },
    setContactsOptInError(state, value) {
      state.contactsOptInError = value;
    },
    set10dlcRegisterData(state, value) {
      state.registrationType10dlc = value.type;
      state.registrationData10dlc = value.data;
      state.isRegistration10dlcIncomplete = value.status != regStatusCompleted;
      state.registrationStatus10dlc = value.status;
      state.registrationStatusData10dlc = value.statusData;
      state.registrationDate10dlc = value.date;
      state.registrationBrandRetries = value.brandRetries;
      state.registrationCampaignRetries = value.campaignRetries;
    },
    setResendOTPError(state, value) {
      state.resendOTPError = value;
      state.resendOTPisError = true;
    },
    setResendOTPisSuccess(state) {
      state.resendOTPisSuccess = true;
    },
    clearResendOTP(state) {
      state.resendOTPError = '';
      state.resendOTPisError = false;
      state.resendOTPisSuccess = false;
    },
    setIsLeadsPlusUser(state, flag) {
      state.isLeadsPlusUser = flag;
    },
    setIsLeadsPlusContact(state, flag) {
      state.isLeadsPlusContact = flag;
    },
  },

  actions: {
    /**
     * Returns the array of conversations with a desired pagination and direction
     * @param {Object} payload, the object intended as a parameter
     * @param {Number} payload.skip, The intended skip to the BE call
     * @param {String} payload.direction, The intended direction to appended to the query
     * @param {Array} payload.searchValues, The array of search values to filter on
     * @returns {Array} The array of conversations
     */
    async getConversations({ state, commit, rootState }, { skip, searchValues, type }) {
      commit('setConversationsInProgress');

      let response;
      const conversationLimit = rootState.preferences.conversationsLimit;
      try {
        const query = {
          $limit: conversationLimit,
          $skip: skip,
          type,
        };

        // Appends the search value string to the query
        if (searchValues && searchValues.length) {
          const orArray = [];
          searchValues.forEach(value => {
            if (value) {
              orArray.push({ first_name: { $like: `%${value}%` } });
              orArray.push({ last_name: { $like: `%${value}%` } });
            }
          });
          if (orArray.length) query.$or = orArray;
        }
        response = await feathers.service('conversations').find({ query });
        if (!response || !response.data) return;

        if (skip) {
          commit('appendConversations', response.data);
        } else {
          commit('setConversations', response.data);
        }
        if (state.conversations.length === response.total) {
          commit('loadedAllConversations');
        } else {
          commit('haveNotLoadedAllConversations');
        }
      } catch (error) {
        commit('setConversationsError', { error: true, message: error.message });
        setTimeout(() => {
          commit('setConversationsError', { error: false, message: '' });
        }, HIDE_ERROR_AFTER_MS);
      }
    },

    /**
     * Returns the array of chat history with a desired pagination and desired conversation
     *
     * @param {Object} payload, the object intended as a parameter
     * @param {Number} payload.offset, The intended offset to the BE call
     * @param {Object} payload.conversation, The intended conversation to query the chats
     *                                       included in that conversation
     *
     * @returns {Array} The array of chat histories
     */
    async getChatHistory({ state, commit, rootState }, { offset, conversation }) {
      if (!conversation) return;

      commit('setConversationsInProgress');

      try {
        const chatLimit = rootState.preferences.conversationsLimit;

        const response = await feathers.service('texts').find({
          query: {
            $limit: chatLimit,
            $skip: offset,
            contact_id: conversation.contact.id,
          },
        });

        if (!response || !response.data) return;

        const videoRegex = new RegExp(' *{{mediavideo_[\\d]{1,}_([\\w-]{1,}|)?}} *', 'g');

        response.data.map(row => {
          if (!row.body) row.body = '';
          const videoVariables = row.body.match(videoRegex);

          row.videos = formatVideos(videoVariables, row);
          row.body = row.body.replace(videoRegex, '');

          return row;
        });

        if (response.data.length < chatLimit) commit('loadedAllChatTexts');
        else commit('haveNotLoadedAllChatTexts');
        if (!offset) {
          commit('setChatHistory', response.data);
        } else {
          commit('appendChatHistory', response.data);
        }
        return response.data;
      } catch (error) {
        commit('setConversationsError', { error: true, message: error.message });
        setTimeout(() => {
          commit('setConversationsError', { error: false, message: '' });
        }, HIDE_ERROR_AFTER_MS);
      }
    },

    /**
     * Sends a new text message to a single contact
     * @param {Object} payload, the object intended as a parameter
     * @param {Number} payload.contactId, The intended contact's id
     * @param {String} payload.phone, The intended phone number in RFC format (e.g +13334445555)
     * @param {String} payload.body, The contents of the text message
     * @returns {Object} The created text message
     */
    async sendMessages({ commit, state }, requestPayload) {
      const {
        contacts,
        body,
        userTimezone,
        imagesURLs,
        actionName,
        sendOptions,
        skip_10dlc_validation,
      } = requestPayload;

      if (!validateContactAnotherCountry(commit, contacts, userTimezone)) return;

      commit('setConversationsInProgress');
      commit('setSendingText', true);

      const payload = {
        body,
        contacts,
        imagesURLs,
        actionName,
        skip_10dlc_validation,
      };

      if (contacts.length > 1) {
        payload.body += '\n\nReply STOP to opt out';
      }

      if (sendOptions && COMMUNICATIONS_MS_SEND_OPTIONS.includes(sendOptions.type)) {
        const schedulePayload = sendOptions.schedule;
        schedulePayload.input = JSON.stringify(payload);
        schedulePayload.contact_ids = contacts.map(to => to.id);
        schedulePayload.sent_from_fe = true;

        try {
          const response = await axios.post(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/scheduled/system_event`,
            schedulePayload,
          );
          const msg = `Successfully scheduled ${response.data.event_created} text message(s)`;
          setConversationSuccess(commit, msg);
        } catch (error) {
          setConversationError(commit, error.data.error || '');
        } finally {
          commit('setSendingText', false);
        }

        return;
      }

      if (actionName === 'bulk') {
        try {
          const response = await axios.post(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/sms/send`,
            {
              body: payload.body,
              contacts,
              imagesURLs,
              actionName,
              skip_10dlc_validation,
              sent_from_fe : true,
            },
            {
              headers: {
                'request-source': 'v2-crm-www-bulk',
              },
            },
          );
          if (response.status === 200) {
            const msg = `Successfully sent ${contacts.length} text message(s)`;
            setConversationSuccess(commit, msg);
          } else {
            setConversationError(commit);
            return 'Error, sent text message(s)';
          }
        } catch (error) {
          if (error.status === 400 && error.data.error === commsAPIPhishingError) {
            commit('setPhishingError', true);
          } else {
            setConversationError(commit, error.data.error || '');
          }
          commit('setSendingText', false);
          return error;
        }
        commit('setSendingText', false);
        return;
      }

      const optInOptions = {
        ORIGINAL_SMS: 'should send original sms',
        SMS_AND_OPT_IN: 'should send opt in',
      };
      if (contacts.length === 1) {
        if (
          !contacts[0].hasOwnProperty('should_send_opt_in') &&
          !contacts[0].hasOwnProperty('should_send_original_sms')
        ) {
          if (sendOptions.optInSelection === optInOptions.ORIGINAL_SMS) {
            contacts[0].should_send_opt_in = false;
            contacts[0].should_send_original_sms = true;
          }

          if (sendOptions.optInSelection === optInOptions.SMS_AND_OPT_IN) {
            contacts[0].should_send_opt_in = true;
            contacts[0].should_send_original_sms = true;
          }
        }
      }

      await Promise.all(sendViaFeather(commit, state, contacts, payload));
    },

    async sendMessagesFromDrawer({ state, rootState, dispatch }, contactId) {
      const message = {
        ...state.textMessageElements,
        userTimezone: rootState.userInformation.user.userTimezone,
      };
      if (!state.contactConversation || !state.contactConversation.id) {
        message.contacts = [
          {
            id: contactId,
            mobile_phone: state.textMessageElements.contacts[0].mobile_phone,
          },
        ];
      }
      await dispatch('sendMessages', message);
    },

    async searchConversationByContactId({ commit }, contactId) {
      commit('setConversationsInProgress');
      let response;
      try {
        response = await feathers.service('texts').find({
          query: {
            contact_id: contactId,
          },
        });

        if (response.data.length) {
          commit('setContactConversation', response.data[0]);

          const responseChat = await feathers.service('texts').find({
            query: { contact_id: contactId },
          });
          commit('setChatHistory', responseChat.data);
        } else {
          const contact = await feathers.service('contacts').get(contactId, {
            query: { $select: ['id', 'first_name', 'last_name'] },
          });
          commit('setContactConversation', { contact });
          commit('setChatHistory', [{ contact }]);
        }
        commit('setConversations', response.data);
      } catch (error) {
        commit('setConversationsError', { error: true, message: error.message });
        setTimeout(() => {
          commit('setConversationsError', { error: false, message: '' });
        }, HIDE_ERROR_AFTER_MS);
      }
    },
    async clearChatHistory({ commit }) {
      commit('clearChatHistory');
    },
    changeSmsLimitModalVisibility({ commit }, payload) {
      commit('setSmsLimitModalVisibility', payload);
    },
    skip10dlcValidation({ commit }) {
      commit('setSkipValidation');
    },
    resetPreviousRequest({ commit }) {
      commit('resetPreviousRequestPayload');
    },
    async checkFor10dlcRegister({ commit }, registrationType = '') {
      try {
        if (!registrationType || registrationType === soleProprietor) {
          let { data } = await axios.get(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/info/sole_proprietor`,
          );
          if (data.crm_user_id) {
            const statusData = data.registration_status_data.String
              ? JSON.parse(data.registration_status_data.String)
              : null;
            commit('set10dlcRegister', true);
            commit('set10dlcRegisterData', {
              type: data.registration_type,
              data: JSON.parse(data.registration_data.String),
              status: data.registration_status,
              statusData,
              date: data.created_at,
              brandRetries: data.brand_retries,
              campaignRetries: data.campaign_retries,
            });
            return;
          }
        }

        if (!registrationType || registrationType === standard) {
          let { data } = await axios.get(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/info/standard`,
          );
          if (data.crm_user_id) {
            const statusData = data.registration_status_data.String
              ? JSON.parse(data.registration_status_data.String)
              : null;
            commit('set10dlcRegister', true);
            commit('set10dlcRegisterData', {
              type: data.registration_type,
              data: JSON.parse(data.registration_data.String),
              status: data.registration_status,
              statusData,
              date: data.created_at,
              brandRetries: data.brand_retries,
              campaignRetries: data.campaign_retries,
            });
            return;
          }
        }

        if (!registrationType || registrationType === lowStandard) {
          let { data } = await axios.get(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/info/low_standard`,
          );
          if (data.crm_user_id) {
            const statusData = data.registration_status_data.String
              ? JSON.parse(data.registration_status_data.String)
              : null;
            commit('set10dlcRegister', true);
            commit('set10dlcRegisterData', {
              type: data.registration_type,
              data: JSON.parse(data.registration_data.String),
              status: data.registration_status,
              statusData,
              date: data.created_at,
              brandRetries: data.brand_retries,
              campaignRetries: data.campaign_retries,
            });
            return;
          }
        }

        commit('set10dlcRegister', false);
        commit('set10dlcRegisterData', {
          type: '',
          data: null,
          status: '',
          statusData: null,
          brandRetries: 0,
          campaignRetries: 0,
        });
      } catch (error) {
        commit('set10dlcRegister', false);
        commit('set10dlcRegisterData', {
          type: '',
          data: null,
          status: '',
          statusData: null,
          brandRetries: 0,
          campaignRetries: 0,
        });
      }
    },
    togglePhishingError({ commit, state }) {
      commit('setPhishingError', !state.hasPhishingError);
    },
    async checkContactsOptInStatuses({ commit, rootState, rootGetters }, contactIds) {
      const contactsTable = rootState.contactsTable;
      let payload;

      if (contactsTable.selectedActionType === 'all') {
        const { selectedEmailListId, excludedContactsIds } = contactsTable;
        let query = rootGetters['contactsTable/queryParamsForAllContacts'];
        if (selectedEmailListId != null) {
          query.contact_list_ids = { $eq: selectedEmailListId };
        }
        if (excludedContactsIds.length > 0) {
          query.excluded_ids = { $ne: [...excludedContactsIds] };
        }

        payload = { query };
      } else {
        if (contactIds && contactIds.length === 0) return;
        payload = { contacts_ids: contactIds };
      }

      try {
        const resp = await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/sms/check-opt-in`,
          payload,
        );

        const addCheckedProperty = resp.data.opt_in_statuses.reduce((acc, val) => {
          val.checked = !(
            val.status === 'pending' ||
            val.status === 'denied' ||
            val.status === 'not_requested'
          );
          return [...acc, val];
        }, []);
        commit('setContactsOptInStatuses', addCheckedProperty);
        commit('setHasNonApproved', resp.data.has_non_approved);
      } catch (error) {
        commit('setContactsOptInError', error);
      }
    },
    async checkContactOptInStatus({ commit }, contactId) {
      let optin = {};
      if (!contactId) return;
      const payload = { contacts_ids: [contactId] };

      try {
        const resp = await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/sms/check-opt-in`,
          payload,
        );
        if (resp.data.opt_in_statuses.length === 0) return;
        optin = resp.data.opt_in_statuses[0].status;
        commit('setContactOptInStatus', optin);
      } catch (error) {
        commit('setContactOptInStatus', '');
      }
    },
    async resendSolePropOTP({ commit }) {
      commit('clearResendOTP');
      try {
        const resp = await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/otp`,
        );
        if (resp.status === 200) {
          commit('setResendOTPisSuccess');
          return;
        }
        commit('setResendOTPError', 'Error re-sending OTP message');
      } catch (error) {
        commit('setResendOTPError', `Error re-sending OTP message: ${error.data.error}`);
      }
    },
    // validatePhoneNumber: validates the given phone number using the lookup endpoint
    async validatePhoneNumber({ }, phoneNumber) {
      try {
        const resp = await axios.get(`${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/sms/lookup/${phoneNumber}?carrier_type=mobile`);
        return resp.data.is_valid;
      } catch (err) {
        return false;
      }
    },
    async checkSoleProprietorRegistration({ commit }) {
      try {
        let { data } = await axios.get(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/info/sole_proprietor`,
        );
        if (data.crm_user_id) {
          const statusData = data.registration_status_data.String
            ? JSON.parse(data.registration_status_data.String)
            : null;
          commit('set10dlcRegister', true);
          commit('set10dlcRegisterData', {
            type: data.registration_type,
            data: JSON.parse(data.registration_data.String),
            status: data.registration_status,
            statusData,
            date: data.created_at,
          });
          return;
        }
        commit('set10dlcRegister', false);
        commit('set10dlcRegisterData', {
          type: undefined,
          data: undefined,
          status: undefined,
          statusData: undefined,
          date: undefined,
        });
      } catch (error) {
        commit('set10dlcRegister', true);
      }
    },
    async getLeadsPlusUserInfo({ commit }) {
      try {
        commit('setIsLeadsPlusUser', false);
        let { data } = await axios.get(
          `${process.env.VUE_APP_INTEGRATIONS}/leadsplus/user`,
        );
        commit('setIsLeadsPlusUser', data.is_leads_plus || false);
      } catch (error) {
        commit('setIsLeadsPlusUser', false);
      }
    },
    async checkLeadsPlusContact({ commit }, contactID) {
      try {
        commit('setIsLeadsPlusContact', false);
        let { data } = await axios.get(
          `${process.env.VUE_APP_INTEGRATIONS}/leadsplus/contact/${contactID}`,
        );
        commit('setIsLeadsPlusContact', data.is_leads_plus_contact || false);
      } catch (error) {
        commit('setIsLeadsPlusContact', false);
      }
    },
  },
};
